import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BarChartIcon from '@mui/icons-material/BarChart';
import CategoryIcon from '@mui/icons-material/Category';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InboxIcon from '@mui/icons-material/Inbox';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RuleIcon from '@mui/icons-material/Rule';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

const DRAWER_WIDTH = 240;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  bottomNav?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Yhteenveto', path: '/', icon: <DashboardIcon />, bottomNav: true },
  { label: 'Tuo tiedosto', path: '/import', icon: <UploadFileIcon /> },
  { label: 'Tarkistettavat', path: '/review', icon: <InboxIcon />, bottomNav: true },
  { label: 'Tapahtumat', path: '/transactions', icon: <ReceiptLongIcon />, bottomNav: true },
  { label: 'Budjetti', path: '/budget', icon: <AccountBalanceWalletIcon />, bottomNav: true },
  { label: 'Raportit', path: '/reports', icon: <BarChartIcon />, bottomNav: true },
  { label: 'Kategoriat', path: '/categories', icon: <CategoryIcon /> },
  { label: 'Tilit', path: '/accounts', icon: <AccountBalanceIcon /> },
  { label: 'Kortit', path: '/cards', icon: <CreditCardIcon /> },
  { label: 'Säännöt', path: '/rules', icon: <RuleIcon /> },
  { label: 'AI-analyysi', path: '/ai', icon: <AutoAwesomeIcon /> },
  { label: 'Asetukset', path: '/settings', icon: <SettingsIcon /> },
];

const BOTTOM_NAV_ITEMS = NAV_ITEMS.filter((item) => item.bottomNav);

export default function Layout({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [reviewCount, setReviewCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, logout } = useAuth();

  // Fetch unresolved review item count
  useEffect(() => {
    api
      .get<{ total: number }>('/api/review-items/summary')
      .then((data) => setReviewCount(data.total))
      .catch(() => {});
  }, [location.pathname]);

  const initials = auth?.email
    ? auth.email.charAt(0).toUpperCase()
    : '?';

  const handleLogout = () => {
    setProfileAnchor(null);
    logout();
  };

  const currentBottomIdx = BOTTOM_NAV_ITEMS.findIndex(
    (item) => item.path === location.pathname,
  );

  const drawerContent = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap fontWeight={700} color="primary">
          HomeLedger
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {NAV_ITEMS.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => {
              navigate(item.path);
              setMobileDrawerOpen(false);
            }}
          >
            <ListItemIcon>
              {item.path === '/review' && reviewCount > 0 ? (
                <Badge badgeContent={reviewCount} color="warning" max={999}>
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          ...(isMobile ? {} : { width: `calc(100% - ${DRAWER_WIDTH}px)`, ml: `${DRAWER_WIDTH}px` }),
        }}
        elevation={1}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
              sx={{ mr: 2 }}
              aria-label="avaa valikko"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {NAV_ITEMS.find((item) => item.path === location.pathname)?.label || 'HomeLedger'}
          </Typography>
          <Tooltip title={auth?.email || ''}>
            <IconButton onClick={(e) => setProfileAnchor(e.currentTarget)} sx={{ p: 0 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.dark', fontSize: 16 }}>
                {initials}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={() => setProfileAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <Typography variant="body2">{auth?.email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Kirjaudu ulos
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          mt: '64px',
          mb: isMobile ? '56px' : 0,
          width: isMobile ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`,
          maxWidth: '100%',
          overflow: 'auto',
        }}
      >
        {children}
      </Box>

      {/* Mobile bottom navigation */}
      {isMobile && (
        <Paper
          sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: theme.zIndex.appBar }}
          elevation={3}
        >
          <BottomNavigation
            value={currentBottomIdx >= 0 ? currentBottomIdx : false}
            onChange={(_, newValue) => {
              navigate(BOTTOM_NAV_ITEMS[newValue].path);
            }}
            showLabels
          >
            {BOTTOM_NAV_ITEMS.map((item) => (
              <BottomNavigationAction
                key={item.path}
                label={item.label}
                icon={
                  item.path === '/review' && reviewCount > 0 ? (
                    <Badge badgeContent={reviewCount} color="warning" max={999}>
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )
                }
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}
